import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
  Fade,
  Chip,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import PageTitle from '../components/PageTitle';

// Sample job data - In a real application, this would come from an API
const jobsData = [
  {
    id: 1,
    title: 'Branch Manager',
    description: 'Responsible for managing all branch activities including loan disbursement, recovery, team supervision, customer relations, and achieving branch targets. Ensures compliance with policies and promotes financial inclusion through micro-lending.',
    positions: 1,
    state: 'Uttar Pradesh',
    city: 'Varanasi',
    email: 'shoorawelfare@gmail.com',
    experience: '1-2 Years'
  },
  {
    id: 2,
    title: 'Trainee Field Officer',
    description: 'The Trainee Field Officer will assist in client identification, group formation, loan processing, recovery, and field documentation under the guidance of senior staff. This is a field-based role aimed at building grassroots-level microfinance experience.',
    positions: 3,
    state: 'Uttar Pradesh',
    city: 'Varanasi',
    email: 'shoorawelfare@gmail.com',
    experience: 'Fresher'
  },
  {
    id: 3,
    title: 'Branch Manager',
    description: 'Responsible for managing all branch activities including loan disbursement, recovery, team supervision, customer relations, and achieving branch targets. Ensures compliance with policies and promotes financial inclusion through micro-lending.',
    positions: 1,
    state: 'Bihar',
    city: 'Patna',
    email: 'shoorawelfare@gmail.com',
    experience: '1-2 Years'
  },
  {
    id: 4,
    title: 'Trainee Field Officer',
    description: 'The Trainee Field Officer will assist in client identification, group formation, loan processing, recovery, and field documentation under the guidance of senior staff. This is a field-based role aimed at building grassroots-level microfinance experience.',
    positions: 3,
    state: 'Bihar',
    city: 'Patna',
    email: 'shoorawelfare@gmail.com',
    experience: 'Fresher'
  },
];

const experienceLevels = ['Fresher', '1-2 Years', '3+ Years'];

const states = [...new Set(jobsData.map(job => job.state))];
const getCities = (state) => [...new Set(jobsData.filter(job => job.state === state).map(job => job.city))];

const JobCard = ({ job }) => {
  const theme = useTheme();
  
  return (
    <>
    <PageTitle title="Carrers" />
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 2
          }}
        >
          {job.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<LocationOnIcon />}
            label={`${job.city}, ${job.state}`}
            size="small"
            sx={{ bgcolor: 'primary.soft' }}
          />
          <Chip
            icon={<WorkIcon />}
            label={job.experience}
            size="small"
            sx={{ bgcolor: 'secondary.soft' }}
          />
          <Chip
            icon={<GroupIcon />}
            label={`${job.positions} ${job.positions > 1 ? 'Positions' : 'Position'}`}
            size="small"
            sx={{ bgcolor: 'success.soft' }}
          />
        </Box>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 3,
            lineHeight: 1.7
          }}
        >
          {job.description}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<EmailIcon />}
          href={`mailto:${job.email}?subject=Application for ${job.title} - ${job.city}`}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Apply Now
        </Button>
      </CardContent>
    </Card>
    </>
   
  );
};

const Careers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [filters, setFilters] = useState({
    search: '',
    state: '',
    city: '',
    experience: ''
  });

  const filteredJobs = useMemo(() => {
    return jobsData.filter(job => {
      const matchesSearch = job.title.toLowerCase().includes(filters.search.toLowerCase());
      const matchesState = !filters.state || job.state === filters.state;
      const matchesCity = !filters.city || job.city === filters.city;
      const matchesExperience = !filters.experience || job.experience === filters.experience;
      
      return matchesSearch && matchesState && matchesCity && matchesExperience;
    });
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      // Reset city when state changes
      if (field === 'state') {
        newFilters.city = '';
      }
      return newFilters;
    });
  };

  return (
    <Box 
      sx={{ 
        pt: { xs: 8, md: 12 },
        pb: { xs: 8, md: 12 },
        bgcolor: 'background.default'
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              color: 'text.primary',
              mb: 2
            }}
          >
            Career Opportunities
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Join our team and make a difference in people's lives through financial inclusion
          </Typography>
        </Box>

        {/* Filters Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: { xs: 4, md: 5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Search jobs by title..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: { xs: 2, md: 0 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={filters.state}
                  label="State"
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                >
                  <MenuItem value="">All States</MenuItem>
                  {states.map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={filters.city}
                  label="City"
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  disabled={!filters.state}
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {filters.state && getCities(filters.state).map(city => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Experience</InputLabel>
                <Select
                  value={filters.experience}
                  label="Experience"
                  onChange={(e) => handleFilterChange('experience', e.target.value)}
                >
                  <MenuItem value="">All Experience Levels</MenuItem>
                  {experienceLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Job Listings */}
        <Grid container spacing={3}>
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <Grid item xs={12} md={6} key={job.id}>
                <Fade in={true} timeout={500}>
                  <Box>
                    <JobCard job={job} />
                  </Box>
                </Fade>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8
                }}
              >
                <Typography
                  variant="h6"
                  color="text.secondary"
                >
                  No jobs found matching your criteria
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default Careers;